import React, { useState, useEffect } from "react";
import {
  makeStyles,
  Paper,
  Grid,
  TextField,
  Button,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider
} from "@material-ui/core";
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Cancel as CancelIcon
} from "@material-ui/icons";
import { toast } from "react-toastify";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(3),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  configCard: {
    marginBottom: theme.spacing(3),
  },
  statusBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: 4,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  statusSuccess: {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.dark,
  },
  statusError: {
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.dark,
  },
}));

const TelnyxConfig = () => {
  const classes = useStyles();

  const [apiKey, setApiKey] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [messagingProfileId, setMessagingProfileId] = useState("");
  
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [hasConfig, setHasConfig] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const { data } = await api.get("/api/telnyx/config");
      const configExists = !!data.apiKey;
      setHasConfig(configExists);
      setApiKey(data.apiKey ? "••••••••••••••••" : "");
      setPhoneNumber(data.phoneNumber || "");
      setMessagingProfileId(data.messagingProfileId || "");
      setEditing(!configExists);
    } catch (error) {
      console.error("Error loading config:", error);
      setEditing(true);
    }
  };

  const handleSaveConfig = async () => {
    try {
      setSaving(true);

      await api.post("/api/telnyx/config", {
        apiKey: apiKey.includes("•") ? undefined : apiKey,
        phoneNumber,
        messagingProfileId
      });

      toast.success("Configuración guardada exitosamente");
      setEditing(false);
      loadConfig();
    } catch (error) {
      console.error("Error saving config:", error);
      toast.error("Error al guardar la configuración");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = () => {
    setEditing(true);
    setApiKey("");
  };

  const handleCancelEdit = () => {
    setEditing(false);
    loadConfig();
  };

  const handleTestConnection = async () => {
    try {
      setTesting(true);
      setConnectionStatus(null);

      const { data } = await api.post("/api/telnyx/config/test");

      setConnectionStatus({
        success: data.success,
        message: data.message
      });

      if (data.success) {
        toast.success("Conexión exitosa con Telnyx");
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      console.error("Error testing connection:", error);
      setConnectionStatus({
        success: false,
        message: error.response?.data?.error || "Error al probar la conexión"
      });
      toast.error("Error al probar la conexión");
    } finally {
      setTesting(false);
    }
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>Configuración de Telnyx</Title>
        <MainHeaderButtonsWrapper>
          {!editing && hasConfig && (
            <Button
              variant="outlined"
              color="primary"
              onClick={handleEdit}
              startIcon={<EditIcon />}
              style={{ marginRight: 8 }}
            >
              Editar
            </Button>
          )}
          {editing && hasConfig && (
            <Button
              variant="outlined"
              onClick={handleCancelEdit}
              startIcon={<CancelIcon />}
              style={{ marginRight: 8 }}
            >
              Cancelar
            </Button>
          )}
          <Button
            variant="outlined"
            color="primary"
            onClick={handleTestConnection}
            disabled={testing || editing}
            startIcon={testing ? <CircularProgress size={20} /> : <RefreshIcon />}
            style={{ marginRight: 8 }}
          >
            Probar Conexión
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveConfig}
            disabled={saving || !editing}
            startIcon={saving ? <CircularProgress size={20} /> : null}
          >
            Guardar
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card className={classes.configCard}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Credenciales de Telnyx
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  Configura tus credenciales de Telnyx para habilitar SMS y llamadas masivas.
                </Typography>

                <TextField
                  fullWidth
                  variant="outlined"
                  label="API Key"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  margin="normal"
                  type="password"
                  helperText="Tu API Key privada de Telnyx"
                  disabled={!editing}
                  placeholder={!editing && hasConfig ? "••••••••••••••••" : ""}
                />

                <TextField
                  fullWidth
                  variant="outlined"
                  label="Número de Teléfono"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  margin="normal"
                  placeholder="+1234567890"
                  helperText="Número de teléfono de Telnyx (formato E.164)"
                  disabled={!editing}
                />

                <TextField
                  fullWidth
                  variant="outlined"
                  label="Messaging Profile ID"
                  value={messagingProfileId}
                  onChange={(e) => setMessagingProfileId(e.target.value)}
                  margin="normal"
                  helperText="ID del perfil de mensajería de Telnyx"
                  disabled={!editing}
                />

                {connectionStatus && (
                  <Box
                    className={`${classes.statusBox} ${
                      connectionStatus.success
                        ? classes.statusSuccess
                        : classes.statusError
                    }`}
                  >
                    {connectionStatus.success ? (
                      <CheckCircleIcon />
                    ) : (
                      <ErrorIcon />
                    )}
                    <Typography variant="body2">
                      {connectionStatus.message}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Instrucciones de Configuración
                </Typography>
                <Typography variant="body2" paragraph>
                  Para obtener tus credenciales de Telnyx:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText
                      primary="1. Crea una cuenta en Telnyx"
                      secondary="Visita https://telnyx.com y regístrate"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="2. Obtén tu API Key"
                      secondary="En el portal, ve a API Keys y crea una nueva"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="3. Compra un número de teléfono"
                      secondary="En Numbers, compra un número para SMS y llamadas"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="4. Crea un Messaging Profile"
                      secondary="En Messaging, crea un perfil y asocia tu número"
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  ¿Qué es Telnyx?
                </Typography>
                <Typography variant="body2" paragraph>
                  Telnyx es una plataforma de comunicaciones que proporciona infraestructura para SMS y llamadas:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="SMS Masivos"
                      secondary="Envía mensajes de texto a múltiples destinatarios"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Llamadas Masivas"
                      secondary="Realiza llamadas automatizadas a múltiples contactos"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Cobertura Global"
                      secondary="Números de teléfono en más de 60 países"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="API Robusta"
                      secondary="Integración fácil con tu aplicación"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Precios Competitivos"
                      secondary="Tarifas bajas para SMS y llamadas"
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>

            <Card style={{ marginTop: 16 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Estado del Sistema
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText
                      primary="Telnyx API"
                      secondary={connectionStatus?.success ? "Conectado" : "No conectado"}
                    />
                    {connectionStatus?.success ? (
                      <CheckCircleIcon color="primary" />
                    ) : (
                      <ErrorIcon color="disabled" />
                    )}
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemText
                      primary="Configuración"
                      secondary={apiKey && phoneNumber ? "Completa" : "Incompleta"}
                    />
                    {apiKey && phoneNumber ? (
                      <CheckCircleIcon color="primary" />
                    ) : (
                      <ErrorIcon color="disabled" />
                    )}
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>
    </MainContainer>
  );
};

export default TelnyxConfig;
